import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSecurityPulse } from "../hooks/useSecurityPulse";

/**
 * DailyPulseGate — first-login-of-the-day security gate.
 *
 * Sits inside <AuthProvider> at the top of the app tree. Whenever the
 * authed user lands on the site (or reloads later in the day),
 * ``/security-pulse/daily-check`` decides whether a fresh pulse is due.
 * If so, the modal fires the moment a *customer-facing* route is
 * entered — auth / verification / privacy pages are exempt so the user
 * can still recover their account if something went wrong.
 *
 * Once the pulse is verified the daily flag flips silently.
 */
const EXEMPT_PATHS = [
  "/", "/login", "/register", "/register/buyer", "/register/seller",
  "/forgot-password", "/reset-password", "/verify", "/registry",
  "/verification-pending", "/access-suspended", "/privacy",
  "/accessibility", "/leadership",
];

export function DailyPulseGate() {
  const { user, api } = useAuth();
  const location = useLocation();
  const pulse = useSecurityPulse(api);
  const [checked, setChecked] = useState(false);

  const check = useCallback(async () => {
    if (!user || typeof user !== "object") return;
    if ((user.role || "").toLowerCase() === "admin") return;
    if (EXEMPT_PATHS.includes(location.pathname)) return;
    try {
      const { data } = await api.get("/security-pulse/daily-check");
      if (data?.needs_pulse && !pulse.isOpen) {
        // Fire and forget — the modal handles the whole flow; we don't
        // need the token, it flips `verified_at` on the server which the
        // next daily-check will notice.
        pulse.request("daily_login", null).catch(() => {});
      }
    } catch {
      /* silent — never block navigation on the check itself */
    }
  }, [api, user, location.pathname, pulse]);

  useEffect(() => {
    if (!user || checked) return;
    if (typeof user !== "object") return;
    setChecked(true);
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return pulse.modal;
}
